import type { GeoLocationPolicy } from '@/components/Functional/utils/geoLocationPolicy'
import type { GeoLocationSnapshot } from '@/components/Functional/utils/locationRetry'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { option } from '../geolocationOption'

type GeoLocationState = {
  lat: number
  lon: number
  accuracy: number
  /** 「表示時の測位が終わった」または「表示時の測位を行わない（skip）」で true。ボタンの skeleton 解除に使う */
  isLoaded: boolean
  /**
   * 位置情報を取得できたか（#3445）。
   * 取得失敗時の lat/lon は 0 のままなので、(0,0) を「未取得」の目印に使うと
   * 有効な座標と区別できない。判定にはこのフラグを使うこと。
   */
  available: boolean
  /** オンデマンド測位（requestLocation）を実行中か（#3583）。ボタンを一時的に disabled にする */
  requesting: boolean
}

type UseGeoLocationOptions = {
  /** 省略時は `fetch`（従来どおりマウント時に測位する） */
  policy?: GeoLocationPolicy
}

const INITIAL_STATE: GeoLocationState = {
  lat: 0,
  lon: 0,
  accuracy: 0,
  isLoaded: false,
  available: false,
  requesting: false,
}

const useGeoLocation = ({ policy = 'fetch' }: UseGeoLocationOptions = {}) => {
  const { t } = useTranslation()
  const [geoLocation, setGeoLocation] = useState<GeoLocationState>(INITIAL_STATE)

  // 非同期ハンドラから最新状態を同期的に読むための ref（クロージャの古い state を避ける）
  const latestRef = useRef(geoLocation)
  useEffect(() => {
    latestRef.current = geoLocation
  }, [geoLocation])
  // 同時に複数ボタンから呼ばれても getCurrentPosition を 1 回にまとめる
  const inFlightRef = useRef<Promise<GeoLocationSnapshot> | null>(null)
  // 文言だけのために requestLocation の identity を変えない
  const tRef = useRef(t)
  useEffect(() => {
    tRef.current = t
  }, [t])

  /**
   * 測位の実体。表示時の測位（effect）とオンデマンド測位（requestLocation）の両方がここを通る。
   * - すでに取得済み（available）ならキャッシュを返し、測位しない
   * - 取得中なら同じ Promise を返す（複数ボタンからの同時呼び出しを 1 回にまとめる）
   * - それ以外は getCurrentPosition を 1 回呼び、結果を state にも反映する
   *   失敗・拒否時は available: false（isLoaded: true）で確定する。
   *   失敗結果はキャッシュしない（端末設定を直した利用者がリロードなしで回復できるようにする）
   * 同期的な setState を持たないため effect から直接呼べる（react-hooks/set-state-in-effect 対策）
   */
  const startRequest = useCallback((): Promise<GeoLocationSnapshot> => {
    const current = latestRef.current
    if (current.available) return Promise.resolve({ lat: current.lat, lon: current.lon, available: true })
    if (inFlightRef.current) return inFlightRef.current

    const promise = getGeolocation(
      tRef.current('spot.access_error_message_02', '位置情報の使用許可がおりませんでした。'),
    )
      .then<GeoLocationSnapshot>((coords) => {
        if (coords) {
          setGeoLocation({
            lat: coords.latitude,
            lon: coords.longitude,
            accuracy: coords.accuracy,
            isLoaded: true,
            available: true,
            requesting: false,
          })
          return { lat: coords.latitude, lon: coords.longitude, available: true }
        }
        // 取得失敗・非対応の場合も isLoaded = true にして、ローディングを終了させる
        setGeoLocation((prev) => ({ ...prev, isLoaded: true, available: false, requesting: false }))
        return { lat: 0, lon: 0, available: false }
      })
      .catch<GeoLocationSnapshot>(() => {
        // パーミッション拒否・タイムアウト等も isLoaded = true にする
        setGeoLocation((prev) => ({ ...prev, isLoaded: true, available: false, requesting: false }))
        return { lat: 0, lon: 0, available: false }
      })
      .finally(() => {
        inFlightRef.current = null
      })
    inFlightRef.current = promise
    return promise
  }, [])

  /**
   * オンデマンド測位（#3583）。ARフォト／音声ガイドで座標が必要になったときに呼ぶ。
   * 実際に測位が走る場合は requesting を立て、完了までボタンをスケルトンにする。
   */
  const requestLocation = useCallback((): Promise<GeoLocationSnapshot> => {
    const current = latestRef.current
    if (!current.available && inFlightRef.current === null) {
      setGeoLocation((prev) => ({ ...prev, requesting: true }))
    }
    return startRequest()
  }, [startRequest])

  useEffect(() => {
    // キャンペーン未ロード（pending）：判断できないので測位しない（isLoaded: false のまま＝ボタンは skeleton）
    // マップなし表示（skip）：表示時は測位しない（#3583）。isLoaded は戻り値で派生させる
    if (policy !== 'fetch') return

    // マップあり表示：従来どおり表示時に測位する
    void startRequest()
  }, [policy, startRequest])

  return {
    ...geoLocation,
    // マップなし表示では測位を待たないため、ボタンを最初から操作可能にする（#3583）
    isLoaded: geoLocation.isLoaded || policy === 'skip',
    requestLocation,
  }
}

export const getGeolocation = (message: string): Promise<GeolocationCoordinates | null> => {
  if ('geolocation' in navigator) {
    return new Promise<GeolocationCoordinates>((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(
        ({ coords }) => {
          resolve(coords)
        },
        reject,
        option,
      )
    })
  }
  alert(message)
  return Promise.resolve(null)
}

export default useGeoLocation
